import { type UseFormReturn } from 'react-hook-form'

import CheckboxField from '@/components/CheckboxField'
import { formatFEIN, formatMoney, formatPhone, formatSSN, formatZip } from '@/components/formatters'
import FormSection from '@/components/FormSection'
import SelectField from '@/components/SelectField'
import TextField from '@/components/TextField'
import { VALID_STATES, type ApplicationFormData } from '@/models/applicationSchema'

const stateOptions = VALID_STATES.map((s) => ({ value: s, label: s }))
const yesNoOptions = [
  { value: 'Yes', label: 'Yes' },
  { value: 'No', label: 'No' },
]
const genderOptions = [
  { value: 'Male', label: 'Male' },
  { value: 'Female', label: 'Female' },
]
const raceOptions = [
  { value: 'Asian', label: 'Asian' },
  { value: 'Black', label: 'Black' },
  { value: 'Hispanic', label: 'Hispanic' },
  { value: 'White', label: 'White' },
  { value: 'Other', label: 'Other' },
]

interface ApplicationFormProps {
  form: UseFormReturn<ApplicationFormData>
  onSubmit: (data: ApplicationFormData) => Promise<void>
  isDisabled: boolean
}

function ApplicationForm({ form, onSubmit, isDisabled }: ApplicationFormProps) {
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = form

  const married = watch('married')
  const bankruptcy = watch('bankruptcy')
  const selfEmployed = watch('selfEmployed')

  return (
    <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-6">
      <ApplicantSection register={register} errors={errors} />
      <MaritalSection register={register} errors={errors} showSpouse={married === 'Yes'} />
      <AddressSection register={register} errors={errors} prefix="home" title="Home Address" />
      <BusinessSection register={register} errors={errors} />
      <CreditSection register={register} errors={errors} />
      <BankruptcySection register={register} errors={errors} showDetails={bankruptcy === 'Yes'} />
      <EmploymentSection register={register} errors={errors} showEmployer={selfEmployed === 'No'} />
      <TermsSection register={register} errors={errors} />
      <SubmitButton isDisabled={isDisabled} />
    </form>
  )
}

type Reg = UseFormReturn<ApplicationFormData>['register']
type Errs = UseFormReturn<ApplicationFormData>['formState']['errors']

function ApplicantSection({ register, errors }: { register: Reg; errors: Errs }) {
  return (
    <FormSection title="Applicant Information">
      <TextField
        label="Applicant Name"
        registration={register('applicantName')}
        error={errors.applicantName?.message}
        required
      />
      <TextField
        label="Date of Birth"
        registration={register('dob')}
        error={errors.dob?.message}
        required
        type="date"
      />
      <TextField
        label="Driver's License #"
        registration={register('driverLicense')}
        error={errors.driverLicense?.message}
        required
      />
      <TextField
        label="Social Security Number"
        registration={register('ssn')}
        error={errors.ssn?.message}
        required
        placeholder="XXX-XX-XXXX"
        format={formatSSN}
      />
      <SelectField
        label="Gender"
        registration={register('gender')}
        options={genderOptions}
        error={errors.gender?.message}
        required
      />
      <SelectField
        label="Race"
        registration={register('race')}
        options={raceOptions}
        error={errors.race?.message}
        required
      />
      <TextField
        label="Current Customer #"
        registration={register('customerNumber')}
        error={errors.customerNumber?.message}
        placeholder="ABC1234567 (optional)"
      />
    </FormSection>
  )
}

function MaritalSection({
  register,
  errors,
  showSpouse,
}: {
  register: Reg
  errors: Errs
  showSpouse: boolean
}) {
  return (
    <FormSection title="Marital Status">
      <SelectField
        label="Married?"
        registration={register('married')}
        options={yesNoOptions}
        error={errors.married?.message}
        required
      />
      {showSpouse ? (
        <>
          <TextField
            label="Spouse Name"
            registration={register('spouseName')}
            error={errors.spouseName?.message}
            required
          />
          <TextField
            label="Spouse Date of Birth"
            registration={register('spouseDOB')}
            error={errors.spouseDOB?.message}
            required
            type="date"
          />
          <TextField
            label="Spouse Driver's License #"
            registration={register('spouseDL')}
            error={errors.spouseDL?.message}
            required
          />
          <TextField
            label="Spouse SSN"
            registration={register('spouseSSN')}
            error={errors.spouseSSN?.message}
            required
            placeholder="XXX-XX-XXXX"
            format={formatSSN}
          />
        </>
      ) : null}
    </FormSection>
  )
}

function AddressSection({
  register,
  errors,
  prefix,
  title,
}: {
  register: Reg
  errors: Errs
  prefix: 'home'
  title: string
}) {
  return (
    <FormSection title={title}>
      <TextField
        label="Address Line 1"
        registration={register(`${prefix}Address1`)}
        error={errors[`${prefix}Address1`]?.message}
        required
        fullWidth
      />
      <TextField
        label="Address Line 2"
        registration={register(`${prefix}Address2`)}
        error={errors[`${prefix}Address2`]?.message}
        fullWidth
      />
      <TextField
        label="City"
        registration={register(`${prefix}City`)}
        error={errors[`${prefix}City`]?.message}
        required
      />
      <SelectField
        label="State"
        registration={register(`${prefix}State`)}
        options={stateOptions}
        error={errors[`${prefix}State`]?.message}
        required
      />
      <TextField
        label="Zip Code"
        registration={register(`${prefix}Zip`)}
        error={errors[`${prefix}Zip`]?.message}
        required
        placeholder="XXXXX or XXXXX-XXXX"
        format={formatZip}
      />
    </FormSection>
  )
}

function BusinessSection({ register, errors }: { register: Reg; errors: Errs }) {
  return (
    <FormSection title="Business Information">
      <TextField
        label="Company Name"
        registration={register('companyName')}
        error={errors.companyName?.message}
        required
      />
      <TextField
        label="Company FEIN"
        registration={register('companyFEIN')}
        error={errors.companyFEIN?.message}
        placeholder="XX-XXXXXXX (optional)"
        format={formatFEIN}
      />
      <TextField
        label="Business Address Line 1"
        registration={register('businessAddress1')}
        error={errors.businessAddress1?.message}
        required
        fullWidth
      />
      <TextField
        label="Business Address Line 2"
        registration={register('businessAddress2')}
        error={errors.businessAddress2?.message}
        fullWidth
      />
      <TextField
        label="City"
        registration={register('businessCity')}
        error={errors.businessCity?.message}
        required
      />
      <SelectField
        label="State"
        registration={register('businessState')}
        options={stateOptions}
        error={errors.businessState?.message}
        required
      />
      <TextField
        label="Zip Code"
        registration={register('businessZip')}
        error={errors.businessZip?.message}
        required
        placeholder="XXXXX or XXXXX-XXXX"
        format={formatZip}
      />
    </FormSection>
  )
}

function CreditSection({ register, errors }: { register: Reg; errors: Errs }) {
  return (
    <FormSection title="Credit Amount / Banking References">
      <TextField
        label="Credit Amount Requested"
        registration={register('creditAmount')}
        error={errors.creditAmount?.message}
        format={formatMoney}
        inputMode="decimal"
        required
        placeholder="$0.00"
      />
      <TextField
        label="Personal Bank Name"
        registration={register('personalBank')}
        error={errors.personalBank?.message}
        required
      />
      <TextField
        label="Business Bank Name"
        registration={register('businessBank')}
        error={errors.businessBank?.message}
        required
      />
    </FormSection>
  )
}

function BankruptcySection({
  register,
  errors,
  showDetails,
}: {
  register: Reg
  errors: Errs
  showDetails: boolean
}) {
  return (
    <FormSection title="Bankruptcy & Debt History">
      <SelectField
        label="Have you ever filed for bankruptcy?"
        registration={register('bankruptcy')}
        options={yesNoOptions}
        error={errors.bankruptcy?.message}
        required
      />
      {showDetails ? (
        <>
          <TextField
            label="Date of Last Bankruptcy"
            registration={register('bankruptcyDate')}
            error={errors.bankruptcyDate?.message}
            required
            type="date"
          />
          <TextField
            label="Jurisdiction"
            registration={register('bankruptcyJurisdiction')}
            error={errors.bankruptcyJurisdiction?.message}
            required
          />
        </>
      ) : null}
      <SelectField
        label="Have you been sued for debt or default in past 2 years?"
        registration={register('suedForDebt')}
        options={yesNoOptions}
        error={errors.suedForDebt?.message}
        required
      />
      <TextField
        label="Current Gross Annual Salary"
        registration={register('grossAnnualSalary')}
        error={errors.grossAnnualSalary?.message}
        format={formatMoney}
        inputMode="decimal"
        required
        placeholder="$0.00"
      />
    </FormSection>
  )
}

function EmploymentSection({
  register,
  errors,
  showEmployer,
}: {
  register: Reg
  errors: Errs
  showEmployer: boolean
}) {
  return (
    <FormSection title="Employment">
      <SelectField
        label="Are you self-employed?"
        registration={register('selfEmployed')}
        options={yesNoOptions}
        error={errors.selfEmployed?.message}
        required
      />
      {showEmployer ? (
        <>
          <TextField
            label="Employer Name"
            registration={register('employerName')}
            error={errors.employerName?.message}
            required
          />
          <TextField
            label="Years at Company"
            registration={register('yearsAtCompany')}
            error={errors.yearsAtCompany?.message}
            required
          />
          <TextField
            label="Job Title"
            registration={register('jobTitle')}
            error={errors.jobTitle?.message}
            required
          />
          <TextField
            label="Supervisor Name"
            registration={register('supervisorName')}
            error={errors.supervisorName?.message}
            required
          />
          <TextField
            label="Supervisor Phone"
            registration={register('supervisorPhone')}
            error={errors.supervisorPhone?.message}
            format={formatPhone}
            inputMode="tel"
            required
          />
        </>
      ) : null}
    </FormSection>
  )
}

function TermsSection({ register, errors }: { register: Reg; errors: Errs }) {
  return (
    <FormSection title="Terms & Conditions">
      <div className="mb-2 text-sm text-gray-600 md:col-span-2">
        <p>
          By submitting this application, I certify that all information provided is true and
          complete to the best of my knowledge. I authorize the company to verify the information
          provided and to obtain credit reports as necessary.
        </p>
      </div>
      <CheckboxField
        label="I agree to the terms and conditions above"
        registration={register('termsAccepted')}
        error={errors.termsAccepted?.message}
      />
    </FormSection>
  )
}

function SubmitButton({ isDisabled }: { isDisabled: boolean }) {
  return (
    <div className="flex justify-end">
      <button
        type="submit"
        disabled={isDisabled}
        className="rounded-md bg-blue-600 px-8 py-3 text-sm font-semibold text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
      >
        {isDisabled ? 'Submitting...' : 'Submit Application'}
      </button>
    </div>
  )
}

export default ApplicationForm
